import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Search, MapPin, Ship, Calendar, Package, CheckCircle, Clock, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TrackingPage() {
  const [searchParams] = useSearchParams();
  const [trackingId, setTrackingId] = useState(searchParams.get('id') || '');
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      setTrackingId(id);
      handleTrack(id);
    }
  }, [searchParams]);

  const handleTrack = async (id = trackingId) => {
    if (!id.trim()) return;
    
    setLoading(true);
    setError(null);
    setSearched(true);
    
    try {
      const response = await axios.get(`${API}/tracking/${id}`);
      setTrackingData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to find tracking information. Please check your tracking ID.');
      setTrackingData(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'in transit':
        return <Ship className="w-6 h-6 text-safety-orange animate-pulse" />;
      default:
        return <Clock className="w-6 h-6 text-blue-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="tracking-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://customer-assets.emergentagent.com/job_maritime-connect-2/artifacts/gxmwasis_step-bg.jpg.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="rsl-container relative z-10">
          <span className="text-safety-orange font-mono text-xs tracking-widest uppercase">
            Real-Time Updates
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight mt-2 mb-6">
            Track Your Shipment
          </h1>
          <p className="text-white/80 text-lg max-w-xl mb-8">
            Enter your tracking number to get real-time updates on your cargo's journey across the globe.
          </p>
          
          {/* Search Form */}
          <div className="bg-white p-6 max-w-2xl" data-testid="tracking-search-form">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Enter tracking number (e.g., RSL123456)"
                  value={trackingId}
                  onChange={(e) => setTrackingId(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleTrack()}
                  className="h-14 text-lg border-2 border-slate-200 focus:border-safety-orange font-mono uppercase"
                  data-testid="tracking-input-main"
                />
              </div>
              <Button
                onClick={() => handleTrack()}
                disabled={loading || !trackingId.trim()}
                className="h-14 px-8 bg-safety-orange hover:bg-orange-600 text-white font-bold uppercase tracking-widest text-sm"
                data-testid="tracking-search-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Search className="w-5 h-5 mr-2" />
                    Track
                  </>
                )}
              </Button>
            </div>
            <p className="text-slate-500 text-sm mt-3">
              Try "RSL123456" or "DEMO" for a sample tracking result
            </p>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-16 lg:py-24">
        <div className="rsl-container">
          {loading && (
            <div className="text-center py-16" data-testid="loading-state">
              <Loader2 className="w-12 h-12 animate-spin text-safety-orange mx-auto mb-4" />
              <p className="text-slate-600">Searching for your shipment...</p>
            </div>
          )}

          {error && searched && !loading && (
            <div className="max-w-xl mx-auto text-center py-16" data-testid="error-state">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-navy mb-2">Tracking Not Found</h3>
              <p className="text-slate-600 mb-6">{error}</p>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-navy text-navy hover:bg-navy hover:text-white">
                  Contact Support
                </Button>
              </Link>
            </div>
          )}

          {trackingData && !loading && (
            <div className="max-w-4xl mx-auto" data-testid="tracking-result">
              {/* Status Card */}
              <div className="bg-white p-8 mb-8 shadow-sm">
                <div className="flex flex-col lg:flex-row justify-between gap-6">
                  <div>
                    <p className="font-mono text-xs text-slate-500 uppercase tracking-wider mb-1">
                      Tracking Number
                    </p>
                    <h2 className="text-2xl font-black text-navy">{trackingData.tracking_id}</h2>
                  </div>
                  <div className="flex items-center gap-3 bg-safety-orange/10 px-6 py-3">
                    {getStatusIcon(trackingData.status)}
                    <div>
                      <p className="font-mono text-xs text-slate-500 uppercase tracking-wider">Status</p>
                      <p className="text-lg font-bold text-navy">{trackingData.status}</p>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-6 mt-8 pt-8 border-t border-slate-200">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-safety-orange flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-mono text-xs text-slate-500 uppercase tracking-wider">Origin</p>
                      <p className="font-bold text-navy">{trackingData.origin}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-mono text-xs text-slate-500 uppercase tracking-wider">Destination</p>
                      <p className="font-bold text-navy">{trackingData.destination}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-mono text-xs text-slate-500 uppercase tracking-wider">ETA</p>
                      <p className="font-bold text-navy">{trackingData.eta}</p>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-slate-200">
                  <div className="flex items-start gap-3">
                    <Ship className="w-5 h-5 text-safety-orange flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-mono text-xs text-slate-500 uppercase tracking-wider">Vessel</p>
                      <p className="font-bold text-navy">{trackingData.vessel_name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Package className="w-5 h-5 text-safety-orange flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-mono text-xs text-slate-500 uppercase tracking-wider">Current Location</p>
                      <p className="font-bold text-navy">{trackingData.current_location}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div className="bg-white p-8 shadow-sm">
                <h3 className="text-xl font-bold text-navy mb-6 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-safety-orange" />
                  Shipment History
                </h3>
                <div className="space-y-6">
                  {trackingData.events?.map((event, index) => (
                    <div key={index} className="relative pl-8" data-testid={`tracking-event-${index}`}>
                      {index !== trackingData.events.length - 1 && (
                        <div className="absolute left-[11px] top-6 w-0.5 h-full bg-slate-200" />
                      )}
                      <div className={`absolute left-0 top-1 w-6 h-6 rounded-full flex items-center justify-center ${
                        index === 0 ? 'bg-safety-orange' : 'bg-slate-200'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          index === 0 ? 'bg-white' : 'bg-slate-400'
                        }`} />
                      </div>
                      <div className="ml-4">
                        <p className="font-mono text-xs text-slate-500">{event.date}</p>
                        <p className="font-bold text-navy">{event.status}</p>
                        <p className="text-slate-600 text-sm">{event.location}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-slate-500 text-sm mt-6 pt-6 border-t border-slate-200">
                  Last updated: {trackingData.last_updated}
                </p>
              </div>
            </div>
          )}

          {!searched && !loading && (
            <div className="text-center py-16">
              <Ship className="w-24 h-24 text-slate-200 mx-auto mb-6" />
              <h3 className="text-xl font-bold text-navy mb-2">Enter Your Tracking Number</h3>
              <p className="text-slate-600 max-w-md mx-auto">
                Use the search above to track your shipment. Your tracking number can be found in your booking confirmation email.
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
